import React, { useState, useEffect } from 'react';
import { portfolioData } from '../data/mock';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Separator } from '../components/ui/separator';
import ToolsCarousel from '../components/ToolsCarousel';
import {
  Download,
  Calendar,
  Mail,
  Linkedin,
  Phone,
  ExternalLink,
  ChevronDown,
  ChevronUp,
  TrendingUp,
  Zap,
  Target,
  DollarSign,
  LineChart,
  Users,
  ArrowRight,
  Menu,
  X,
  Moon,
  Sun
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const iconMap = {
  TrendingUp,
  Zap,
  Target,
  DollarSign,
  LineChart,
  Users
};

const Portfolio = () => {
  const [activeSection, setActiveSection] = useState('hero');
  const [selectedProject, setSelectedProject] = useState(null);
  const [currentStrength, setCurrentStrength] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [calendlyOpen, setCalendlyOpen] = useState(false);
  const [theme, setTheme] = useState(() => {
    return localStorage.getItem('portfolio-theme') || 'light';
  });

  useEffect(() => {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    localStorage.setItem('portfolio-theme', theme);
  }, [theme]);

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentStrength((prev) => (prev + 1) % portfolioData.coreStrengths.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['hero', 'highlights', 'projects', 'tools', 'skills', 'testimonials', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({
        top: elementPosition - offset,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const navLinks = [
    { id: 'highlights', label: 'Highlights' },
    { id: 'projects', label: 'Work' },
    { id: 'tools', label: 'Tools' },
    { id: 'skills', label: 'Skills' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 transition-colors duration-300">
      {/* Sticky Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <button
              onClick={() => scrollToSection('hero')}
              className="text-xl font-semibold bg-gradient-to-r from-teal-600 to-violet-600 bg-clip-text text-transparent hover:opacity-80 transition-opacity"
            >
              {portfolioData.personal.name}
            </button>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-1">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeSection === link.id
                      ? 'bg-gradient-to-r from-teal-600 to-violet-600 text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {link.label}
                </button>
              ))}
              <button
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className="ml-2 p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? (
                  <Moon className="h-5 w-5 text-slate-600" />
                ) : (
                  <Sun className="h-5 w-5 text-slate-300" />
                )}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="flex md:hidden items-center gap-2">
              <button
                onClick={() => setTheme(theme === 'light' ? 'dark' : 'light')}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Toggle theme"
              >
                {theme === 'light' ? (
                  <Moon className="h-5 w-5 text-slate-600" />
                ) : (
                  <Sun className="h-5 w-5 text-slate-300" />
                )}
              </button>
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="p-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                aria-label="Toggle menu"
              >
                {mobileMenuOpen ? (
                  <X className="h-6 w-6 text-slate-600 dark:text-slate-300" />
                ) : (
                  <Menu className="h-6 w-6 text-slate-600 dark:text-slate-300" />
                )}
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 space-y-2">
              {navLinks.map((link) => (
                <button
                  key={link.id}
                  onClick={() => scrollToSection(link.id)}
                  className={`w-full text-left px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                    activeSection === link.id
                      ? 'bg-gradient-to-r from-teal-600 to-violet-600 text-white'
                      : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
                  }`}
                >
                  {link.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section id="hero" className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="space-y-3">
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-slate-900 dark:text-white leading-tight">
                  {portfolioData.personal.name}
                </h1>
                <p className="text-xl md:text-2xl text-slate-600 dark:text-slate-300 font-medium">
                  {portfolioData.personal.role}
                </p>
                <div className="pt-1">
                  <span className="inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-emerald-100 to-teal-100 dark:from-emerald-900/30 dark:to-teal-900/30 rounded-full border border-emerald-300 dark:border-emerald-700">
                    <span className="relative flex h-2 w-2">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                    </span>
                    <span className="text-xs font-semibold text-emerald-700 dark:text-emerald-300">
                      Available for opportunities
                    </span>
                  </span>
                </div>
              </div>

              <p className="text-lg text-slate-600 dark:text-slate-400 leading-relaxed">
                {portfolioData.personal.tagline}
              </p>

              <div className="flex flex-wrap gap-4 pt-4">
                <Button
                  onClick={() => scrollToSection('projects')}
                  className="bg-gradient-to-r from-teal-600 to-violet-600 hover:from-teal-700 hover:to-violet-700 text-white font-semibold px-8 py-3 h-[52px] rounded-full transition-all duration-200 hover:scale-105 text-base"
                >
                  <ArrowRight className="mr-2 h-5 w-5" />
                  View my work
                </Button>
                <a
                  href={portfolioData.personal.linkedin}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button
                    className="bg-gradient-to-r from-teal-600 to-violet-600 hover:from-teal-700 hover:to-violet-700 text-white font-semibold px-8 py-3 h-[52px] rounded-full transition-all duration-200 hover:scale-105 text-base"
                  >
                    <Linkedin className="mr-2 h-5 w-5" />
                    LinkedIn
                  </Button>
                </a>
                <Button
                  onClick={() => setCalendlyOpen(true)}
                  variant="outline"
                  className="font-semibold px-8 py-3 h-[52px] rounded-full border-2 border-slate-300 dark:border-slate-700 hover:border-teal-600 dark:hover:border-teal-500 transition-all duration-200 text-base"
                >
                  <Calendar className="mr-2 h-5 w-5" />
                  Book a 1-on-1
                </Button>
              </div>
            </div>

            <div className="flex justify-center md:justify-end">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-teal-400 to-violet-400 rounded-3xl blur-2xl opacity-20 animate-pulse"></div>
                <div className="relative w-80 h-80 md:w-96 md:h-96 rounded-3xl shadow-2xl overflow-hidden">
                  <img
                    src={portfolioData.personal.headshot}
                    alt={portfolioData.personal.name}
                    className="w-full h-full object-cover object-center"
                    style={{ objectPosition: '50% 30%' }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highlights Section */}
      <section id="highlights" className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Impact Highlights
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-violet-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioData.highlights.map((highlight) => {
              const Icon = iconMap[highlight.logo];
              return (
                <Card
                  key={highlight.id}
                  className="group hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border-2 hover:border-gradient"
                >
                  <CardContent className="p-6">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-gradient-to-br from-teal-100 to-violet-100 dark:from-teal-900/30 dark:to-violet-900/30 rounded-xl">
                        <Icon className="h-8 w-8 text-teal-600 dark:text-teal-400" />
                      </div>
                      <div className="flex-1">
                        <h3 className="text-2xl font-bold bg-gradient-to-r from-teal-700 to-violet-700 bg-clip-text text-transparent mb-2">
                          {highlight.metric}
                        </h3>
                        <p className="text-sm text-slate-600 dark:text-slate-400 leading-relaxed">
                          {highlight.description}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Projects Section - Moved before Experience */}
      <section id="projects" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Selected Work
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-violet-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {portfolioData.projects.map((project) => (
              <Card
                key={project.id}
                className="group cursor-pointer hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
                onClick={() => setSelectedProject(project)}
              >
                <CardContent className="p-0">
                  <div className="h-2 bg-gradient-to-r from-teal-600 to-violet-600"></div>
                  <div className="p-6">
                    <Badge className="mb-3 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 border-0 font-medium">
                      {project.category}
                    </Badge>
                    <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-3 group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                      {project.title}
                    </h3>
                    <div className="mb-4">
                      <p className="text-3xl font-bold bg-gradient-to-r from-teal-700 to-violet-700 bg-clip-text text-transparent">
                        {project.metric}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags.map((tag, idx) => (
                        <Badge
                          key={idx}
                          variant="outline"
                          className="text-xs border-slate-300 dark:border-slate-700"
                        >
                          {tag}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center text-teal-600 dark:text-teal-400 text-sm font-medium group-hover:gap-2 transition-all">
                      View Details
                      <ArrowRight className="h-4 w-4 ml-1 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Tools & Software Section */}
      <section id="tools" className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900/50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Tools & Software
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-violet-600 mx-auto rounded-full"></div>
            <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
              Proficient across industry-leading platforms and technologies
            </p>
          </div>

          <ToolsCarousel tools={portfolioData.toolsAndSoftware} />
        </div>
      </section>

      {/* Project Modal */}
      <Dialog open={!!selectedProject} onOpenChange={() => setSelectedProject(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedProject && (
            <>
              <DialogHeader>
                <div className="space-y-3">
                  <Badge className="bg-gradient-to-r from-teal-600 to-violet-600 text-white border-0">
                    {selectedProject.category}
                  </Badge>
                  <DialogTitle className="text-3xl font-bold text-slate-900 dark:text-white">
                    {selectedProject.title}
                  </DialogTitle>
                  <p className="text-lg font-semibold text-teal-600 dark:text-teal-400">
                    {selectedProject.company}
                  </p>
                  <p className="text-2xl font-bold bg-gradient-to-r from-teal-700 to-violet-700 bg-clip-text text-transparent">
                    {selectedProject.metric}
                  </p>
                </div>
              </DialogHeader>
              <DialogDescription asChild>
                <div className="space-y-6 text-slate-700 dark:text-slate-300">
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-teal-600"></div>
                      Situation
                    </h4>
                    <p className="text-sm leading-relaxed">{selectedProject.situation}</p>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-indigo-600"></div>
                      Task
                    </h4>
                    <p className="text-sm leading-relaxed">{selectedProject.task}</p>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-violet-600"></div>
                      Action
                    </h4>
                    <p className="text-sm leading-relaxed">{selectedProject.action}</p>
                  </div>
                  <Separator />
                  <div>
                    <h4 className="font-bold text-slate-900 dark:text-white mb-2 flex items-center gap-2">
                      <div className="h-1 w-1 rounded-full bg-teal-600"></div>
                      Result
                    </h4>
                    <p className="text-sm leading-relaxed">{selectedProject.result}</p>
                  </div>
                  <div className="flex flex-wrap gap-2 pt-4">
                    {selectedProject.tags.map((tag, idx) => (
                      <Badge
                        key={idx}
                        variant="outline"
                        className="border-slate-300 dark:border-slate-700"
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
              </DialogDescription>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Calendly Modal */}
      <Dialog open={calendlyOpen} onOpenChange={setCalendlyOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-hidden p-0">
          <div className="bg-gradient-to-r from-teal-600 to-violet-600 p-6 text-white">
            <Calendar className="h-8 w-8 mx-auto mb-3" />
            <DialogTitle className="text-2xl font-bold text-center text-white">
              Schedule a 1-on-1 Meeting
            </DialogTitle>
            <p className="text-center text-teal-50 mt-2">
              Book a 30-minute consultation to discuss opportunities
            </p>
          </div>
          <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
            <iframe
              src={portfolioData.personal.calendly}
              width="100%"
              height="700px"
              frameBorder="0"
              title="Schedule a meeting"
              className="rounded-lg"
            ></iframe>
          </div>
        </DialogContent>
      </Dialog>

      {/* Skills & Expertise Section */}
      <section id="skills" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Skills & Expertise
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-violet-600 mx-auto rounded-full"></div>
          </div>

          {/* Technical Skills */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-6">
              <div className="h-6 w-1 bg-gradient-to-b from-teal-600 to-cyan-600 rounded-full"></div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
                Technical Skills
              </h3>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-3 text-slate-700 dark:text-slate-300">
              {portfolioData.skillsAndExpertise.technical.map((skill, idx) => (
                <div key={idx} className="flex items-center gap-2 group">
                  <span className="text-teal-600 dark:text-teal-400 font-medium">•</span>
                  <span className="text-sm md:text-base group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                    {skill}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Soft Skills */}
          <div>
            <div className="flex items-center gap-3 mb-6">
              <div className="h-6 w-1 bg-gradient-to-b from-violet-600 to-purple-600 rounded-full"></div>
              <h3 className="text-xl md:text-2xl font-bold text-slate-900 dark:text-white">
                Soft Skills
              </h3>
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-3 text-slate-700 dark:text-slate-300">
              {portfolioData.skillsAndExpertise.soft.map((skill, idx) => (
                <div key={idx} className="flex items-center gap-2 group">
                  <span className="text-violet-600 dark:text-violet-400 font-medium">•</span>
                  <span className="text-sm md:text-base group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                    {skill}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-16 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Testimonials
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-violet-600 mx-auto rounded-full"></div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {portfolioData.testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="mb-4">
                    <div className="text-4xl text-teal-600 dark:text-teal-400 mb-2">"</div>
                    <p className="text-slate-700 dark:text-slate-300 italic leading-relaxed text-sm">
                      {testimonial.quote}
                    </p>
                  </div>
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <p className="font-semibold text-slate-900 dark:text-white text-sm">
                      {testimonial.author}
                    </p>
                    <p className="text-xs text-slate-600 dark:text-slate-400">
                      {testimonial.role}
                    </p>
                    <p className="text-xs text-teal-600 dark:text-teal-400 font-medium mt-1">
                      {testimonial.company}
                    </p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-bold text-slate-900 dark:text-white mb-4">
              Let's Connect
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-teal-600 to-violet-600 mx-auto rounded-full"></div>
            <p className="mt-6 text-lg text-slate-600 dark:text-slate-400">
              Interested in discussing RevOps strategies or exploring opportunities?
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <a
              href={`mailto:${portfolioData.personal.email}`}
              className="group"
            >
              <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
                <CardContent className="p-6 flex items-center gap-4 h-full">
                  <div className="p-3 bg-gradient-to-br from-teal-100 to-violet-100 dark:from-teal-900/30 dark:to-violet-900/30 rounded-xl flex-shrink-0">
                    <Mail className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Email</p>
                    <p className="font-semibold text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors truncate">
                      {portfolioData.personal.email}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </a>

            <a
              href={portfolioData.personal.linkedin}
              target="_blank"
              rel="noopener noreferrer"
              className="group"
            >
              <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
                <CardContent className="p-6 flex items-center gap-4 h-full">
                  <div className="p-3 bg-gradient-to-br from-teal-100 to-violet-100 dark:from-teal-900/30 dark:to-violet-900/30 rounded-xl flex-shrink-0">
                    <Linkedin className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">LinkedIn</p>
                    <p className="font-semibold text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors flex items-center gap-2">
                      Connect with me
                      <ExternalLink className="h-4 w-4" />
                    </p>
                  </div>
                </CardContent>
              </Card>
            </a>

            <button
              onClick={() => setCalendlyOpen(true)}
              className="group w-full text-left"
            >
              <Card className="hover:shadow-lg transition-all duration-300 hover:-translate-y-1 h-full">
                <CardContent className="p-6 flex items-center gap-4 h-full">
                  <div className="p-3 bg-gradient-to-br from-teal-100 to-violet-100 dark:from-teal-900/30 dark:to-violet-900/30 rounded-xl flex-shrink-0">
                    <Calendar className="h-6 w-6 text-teal-600 dark:text-teal-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-1">Schedule</p>
                    <p className="font-semibold text-slate-900 dark:text-white group-hover:text-teal-600 dark:group-hover:text-teal-400 transition-colors">
                      Book a 1-on-1 Meeting
                    </p>
                  </div>
                </CardContent>
              </Card>
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 px-4 sm:px-6 lg:px-8 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="flex items-center gap-6">
              <a
                href={portfolioData.personal.linkedin}
                target="_blank"
                rel="noopener noreferrer"
                className="text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href={`mailto:${portfolioData.personal.email}`}
                className="text-slate-600 dark:text-slate-400 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
              >
                <Mail className="h-5 w-5" />
              </a>
            </div>
            <div className="text-center md:text-right">
              <p className="text-sm text-slate-600 dark:text-slate-400">
                © {new Date().getFullYear()} {portfolioData.personal.name}. All rights reserved.
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-500 mt-1">
                Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Portfolio;
